// frontend/src/components/Layout/ClientLayout.jsx
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useNotifications from '../../hooks/useNotifications';
import { useNotificationAlert } from '../../context/NotificationAlertContext';
import NotificationPanel from '../notifications/NotificationPanel';
import NotificationAlert from '../notifications/NotificationAlert';
import logo from '../../assets/logoproxy.png';
import {
  BanknotesIcon,
  BellIcon,
  ChevronDownIcon,
  HomeIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Squares2X2Icon,
  ArrowsRightLeftIcon,
  Cog6ToothIcon,
  CubeIcon,
} from '@heroicons/react/24/solid';
import './ClientLayout.css';

const ClientLayout = ({ children }) => {
  const navigate   = useNavigate();
  const location   = useLocation();
  const profileRef = useRef(null);

  const [user, setUser]               = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showPanel, setShowPanel]     = useState(false);

  const { notifications, unreadCount, markRead, markAllRead, remove } = useNotifications();
  const { triggerAlert } = useNotificationAlert();

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUser(JSON.parse(u));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target))
        setShowProfile(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleNotifClick = (notif) => {
    triggerAlert(notif.title);
    if (notif.target_url) navigate(notif.target_url);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
    navigate('/login/client');
  };

  /* ── Navigation ── */
  const navLinks = [
    { path: '/dashboard/client',              label: 'Accueil'    },
    { path: '/dashboard/client/services',     label: 'Services'   },
    { path: '/dashboard/client/buy',          label: 'Wallet'     },
    { path: '/dashboard/client/transactions', label: 'Historique' },
    { path: '/dashboard/client/settings',     label: 'Support'    },
  ];

  const iconStyle = { width: 18, height: 18, flexShrink: 0 };
  const sideItems = [
    { path: '/dashboard/client',              icon: <Squares2X2Icon style={iconStyle} />,     label: 'Dashboard'  },
    { path: '/dashboard/client/services',     icon: <CubeIcon style={iconStyle} />,           label: 'Services'   },
    { path: '/dashboard/client/buy',          icon: <BanknotesIcon style={iconStyle} />,       label: 'Wallet'     },
    { path: '/dashboard/client/transactions', icon: <ArrowsRightLeftIcon style={iconStyle} />, label: 'Historique' },
    { path: '/dashboard/client/settings',     icon: <Cog6ToothIcon style={iconStyle} />,       label: 'Support'    },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="cl-layout">

      {/* ═══════════════════════════════════════
          TOP NAVBAR
      ═══════════════════════════════════════ */}
      <header className="cl-navbar">

        {/* Brand */}
        <div className="cl-brand" onClick={() => navigate('/dashboard/client')}>
          <img src={logo} alt="POINTPAY" className="logo-img" />
        </div>

        {/* Horizontal nav links */}
        <nav className="cl-nav-links">
          {navLinks.map(link => (
            <button
              key={link.path}
              className={`cl-nav-link ${isActive(link.path) ? 'active' : ''}`}
              onClick={() => navigate(link.path)}
            >
              {link.label}
              {isActive(link.path) && (
                <motion.div
                  className="cl-nav-link-bar"
                  layoutId="navBar"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
            </button>
          ))}
        </nav>

        {/* Right actions */}
        <div className="cl-nav-right">

          {/* Notifications bell */}
          <motion.button
            className={`cl-nav-icon-btn${showPanel ? ' active' : ''}`}
            onClick={() => { setShowPanel(true); setShowProfile(false); }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.93 }}
            title="Notifications"
          >
            <BellIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
            {unreadCount > 0 && (
              <motion.span
                className="cl-badge"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </motion.span>
            )}
          </motion.button>

          {/* Avatar + dropdown */}
          <div className="cl-avatar-wrap" ref={profileRef}>
            <motion.button
              className={`cl-avatar-btn${showProfile ? ' active' : ''}`}
              onClick={() => setShowProfile(v => !v)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <div className="cl-avatar">
                {user?.first_name?.[0]?.toUpperCase() || 'C'}
              </div>
              <div className="cl-avatar-info">
                <span className="cl-avatar-name">{user?.first_name} {user?.last_name}</span>
                <span className="cl-avatar-role">Client</span>
              </div>
              <motion.div
                className="cl-chevron"
                animate={{ rotate: showProfile ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDownIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
              </motion.div>
            </motion.button>

            <AnimatePresence>
              {showProfile && (
                <motion.div
                  className="cl-dropdown"
                  initial={{ opacity: 0, y: -8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 360, damping: 26 } }}
                  exit={{ opacity: 0, y: -8, scale: 0.96, transition: { duration: 0.14 } }}
                >
                  <div className="cl-dd-header">
                    <div className="cl-dd-avatar">
                      {user?.first_name?.[0]?.toUpperCase() || 'C'}
                    </div>
                    <div>
                      <div className="cl-dd-name">{user?.first_name} {user?.last_name}</div>
                      <div className="cl-dd-email">{user?.email}</div>
                    </div>
                  </div>
                  <div className="cl-dd-sep" />
                  <button className="cl-dd-item" onClick={() => { navigate('/dashboard/client'); setShowProfile(false); }}>
                    <HomeIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Tableau de bord
                  </button>
                  <button className="cl-dd-item" onClick={() => { navigate('/dashboard/client/settings'); setShowProfile(false); }}>
                    <UserCircleIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Paramètres
                  </button>
                  <div className="cl-dd-sep" />
                  <button className="cl-dd-item cl-dd-danger" onClick={handleLogout}>
                    <ArrowRightOnRectangleIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Déconnexion
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </header>

      {/* ═══════════════════════════════════════
          BODY  =  Mini sidebar  +  Content
      ═══════════════════════════════════════ */}
      <div className="cl-body">

        {/* Mini icon sidebar */}
        <aside className="cl-sidebar">
          {sideItems.map(item => (
            <div key={item.path} className="cl-side-item-wrap">
              <motion.button
                className={`cl-side-item${isActive(item.path) ? ' active' : ''}`}
                onClick={() => navigate(item.path)}
                whileHover={{ scale: 1.12 }}
                whileTap={{ scale: 0.92 }}
              >
                {item.icon}
              </motion.button>
              <div className="cl-side-tooltip">{item.label}</div>
            </div>
          ))}
        </aside>

        {/* Main content */}
        <main className="cl-main">
          <NotificationAlert />
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              className="cl-page"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Notifications panel */}
      <NotificationPanel
        open={showPanel}
        onClose={() => setShowPanel(false)}
        notifications={notifications}
        unreadCount={unreadCount}
        onMarkRead={markRead}
        onMarkAllRead={markAllRead}
        onRemove={remove}
        onNotifClick={handleNotifClick}
      />
    </div>
  );
};

export default ClientLayout;
